import React, { useState, useEffect, useRef } from "react";
import config from "../config";

// ─── Ticket Number Generator ──────────────────────────────────────────────────
const generateTicketNumber = () => {
  const now = new Date();
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const h = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const sc = String(now.getSeconds()).padStart(2, "0");
  const ms = String(now.getMilliseconds()).padStart(3, "0");
  return `TICKET-${y}${mo}${d}-${h}${mi}${sc}-${ms}`;
};

// ─── Email Sender ─────────────────────────────────────────────────────────────
const sendTicketEmail = (ticketNumber, form) => {
  if (!form.Email) return;
  const payload = {
    toEmail: form.Email,
    toName: form.Reporter || "User",
    subject: `[New Ticket] ${ticketNumber} - ${form.Subject}`,
    body:
      `Hi ${form.Reporter || "User"},\n\n` +
      `Your support ticket has been successfully created!\n\n` +
      `TICKET DETAILS\n` +
      `Ticket Number  : ${ticketNumber}\n` +
      `Subject        : ${form.Subject || "N/A"}\n` +
      `Category       : ${form.Category || "N/A"}\n` +
      `Priority       : ${form.Priority || "N/A"}\n` +
      `Severity       : ${form.Severity || "N/A"}\n` +
      `Urgency        : ${form.Urgency || "N/A"}\n` +
      `Assignees      : ${form.Assignees || "Unassigned"}\n` +
      `Team           : ${form.Team || "N/A"}\n` +
      `Status         : Open\n` +
      `Created On     : ${new Date().toLocaleString()}\n\n` +
      `DESCRIPTION\n${form.Description || "N/A"}\n\n` +
      `You can log in to the User Portal to view and track your ticket.\n\n` +
      `Best regards,\nOrca Helpdesk Team\n\n` +
      `DO NOT REPLY TO THIS EMAIL.\n` +
      `This is an automated message. Replies will not be monitored.\n` +
      `If you have concerns, please log in to the User Portal instead.`,
  };
  fetch(`${config.API_BASE_URL}/Email/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then((r) =>
      r.ok
        ? console.log(`%c✉ Email sent → ${form.Email}`, "color:#4ade80;font-weight:bold")
        : console.warn("Email API error:", r.status)
    )
    .catch((e) => console.warn("Email failed:", e.message));
};

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const Icon = {
  Ticket: ({ size = 22, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
      <path d="M13 5v2M13 17v2M13 11v2"/>
    </svg>
  ),
  Basics: ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3"/>
      <path d="M3 9h18M9 21V9"/>
    </svg>
  ),
  Details: ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
    </svg>
  ),
  Assignment: ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/>
      <path d="M20 21a8 8 0 1 0-16 0"/>
    </svg>
  ),
  Description: ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
  ),
  Check: ({ size = 40, color = "#4bce97" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <path d="M10 20l7 7 13-14" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Close: ({ size = 14, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Paperclip: ({ size = 28, color = "#4a6a8a" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
    </svg>
  ),
  ArrowLeft: ({ size = 14, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6"/>
    </svg>
  ),
  ArrowRight: ({ size = 14, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 18 6-6-6-6"/>
    </svg>
  ),
  Mail: ({ size = 14, color = "#4a6a8a" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  FileDoc: ({ size = 24, color = "#8aaccc" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
      <polyline points="14,2 14,8 20,8"/>
    </svg>
  ),
};

// ─── Step config ──────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: "Basics",      IconComp: Icon.Basics },
  { id: 1, label: "Details",     IconComp: Icon.Details },
  { id: 2, label: "Assignment",  IconComp: Icon.Assignment },
  { id: 3, label: "Description", IconComp: Icon.Description },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreateTicketFileModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [createdTicketNum, setCreatedTicketNum] = useState("");

  const [affectedServices, setAffectedServices] = useState([]);
  const [components, setComponents] = useState([]);
  const [reporters, setReporters] = useState([]);
  const [teams, setTeams] = useState([]);

  const [form, setForm] = useState({
    Subject: "", IssueType: "", Summary: "", Email: "",
    Affected: "", Components: "", Reporter: "", Estimate: "",
    Linked: "None", Severity: "Sev-0", Urgency: "Medium",
    Assignees: "", Deadline: "", Team: "", Category: "General - IT Support",
    Description: "", Priority: "Low",
  });
  const [attachment, setAttachment] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    const safe = (p) => p.then((r) => (r.ok ? r.json() : [])).catch(() => []);
    Promise.all([
      safe(fetch(`${config.API_BASE_URL}/Affected`)),
      safe(fetch(`${config.API_BASE_URL}/Components`)),
      safe(fetch(`${config.API_BASE_URL}/Admin`)),
      safe(fetch(`${config.API_BASE_URL}/Teams`)),
    ]).then(([aff, comp, rep, tm]) => {
      setAffectedServices(aff);
      setComponents(comp);
      setReporters(rep);
      setTeams(tm);
    });
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handle = (e) => set(e.target.name, e.target.value);

  const canNext = () => {
    if (step === 0) return form.Subject.trim() !== "";
    if (step === 3) return form.Description.trim() !== "";
    return true;
  };

  const handleSubmit = async () => {
    if (!form.Subject.trim() || !form.Description.trim()) return;
    setSubmitting(true);
    const ticketNumber = generateTicketNumber();
    try {
      const fd = new FormData();
      const now = new Date().toISOString();
      const deadline = form.Deadline
        ? new Date(form.Deadline).toISOString()
        : (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString(); })();

      const fields = {
        TicketNumber: ticketNumber,
        Subject: form.Subject || "No Subject",
        Assignees: form.Assignees || "Unassigned",
        Name: form.Reporter || "Anonymous",
        Contact: "N/A",
        Email: form.Email || "no-email@system.local",
        Category: form.Category || "General - IT Support",
        Description: form.Description || "No description provided",
        Priority: form.Priority || "Low",
        UserId: "system",
        Status: "Open",
        Notes: "Created via Web Portal",
        Subtask: "None",
        Followup: "false",
        Done: "false",
        Team: form.Team || "General Support",
        Origin: "Web Portal",
        Tags: "web-submission",
        InternalNotes: "Auto-created from web form",
        DescriptionNotes: "User-submitted ticket",
        ActivityDescription: `Ticket created at ${new Date().toLocaleString()}`,
        CreatedAt: now, Timestamp: now, DateStarted: now, DateUpdated: now,
        Deadline: deadline,
        TimeSpent: "0", TimeRemaining: "0", TimeGoal: "48 hours",
        Calendar: "Standard Hours",
        JQL: `priority = ${form.Priority || "Medium"}`,
        Condition: "New",
        OpenIncidents: "0", MyIncidents: "0", MajorIncidents: "0",
        Components: form.Components || "General",
        Summary: form.Summary || form.Subject,
        Reporter: form.Reporter || "System",
        Estimate: form.Estimate || "TBD",
        Linked: form.Linked || "None",
        Severity: form.Severity || "Sev-3",
        Urgency: form.Urgency || "Medium",
        IssueType: form.IssueType || "General Request",
        Affected: form.Affected || "N/A",
        WorkDescription: "Pending initial review",
        OpenDescription: `Ticket opened on ${new Date().toLocaleDateString()}`,
        PendingDescription: "", CompleteDescription: "",
        ResolvedDescription: "", CloseDescription: "",
        Reason: "New ticket submission",
      };

      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      if (attachment) fd.append("attachment", attachment);

      const res = await fetch(`${config.API_BASE_URL}/Tickets`, { method: "POST", body: fd });
      if (!res.ok) {
        const txt = await res.text();
        let msg = "Failed to create ticket";
        try {
          const j = JSON.parse(txt);
          msg = j.errors
            ? Object.entries(j.errors).map(([f, m]) => `${f}: ${m.join(", ")}`).join("\n")
            : j.title || msg;
        } catch {}
        throw new Error(msg);
      }

      sendTicketEmail(ticketNumber, form);
      setCreatedTicketNum(ticketNumber);
      setDone(true);
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setAttachment(f);
  };

  const selectedReporter = reporters.find((r) => r.fullNames === form.Reporter);

  // ── Success screen ────────────────────────────────────────────────────────
  if (done) {
    return (
      <Overlay onClose={onClose}>
        <div style={s.successBox}>
          {/* Animated rings */}
          <div style={{ position: "relative", marginBottom: 8 }}>
            <div style={s.successRingOuter} />
            <div style={s.successRingInner}>
              <Icon.Check size={36} color="#4bce97" />
            </div>
          </div>
          <h2 style={s.successTitle}>Ticket Created!</h2>
          <div style={s.successNum}>{createdTicketNum}</div>
          <p style={s.successSub}>
            {form.Email ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                <Icon.Mail size={13} color="#4a6a8a" /> Confirmation sent to {form.Email}
              </span>
            ) : (
              "No email on file — notification skipped"
            )}
          </p>
          <button style={s.successBtn} onClick={onClose}>Done</button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <SpinStyle />
      <div style={s.modal}>

        {/* ── Header ── */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            {/* Polished icon box */}
            <div style={s.headerIconBox}>
              <Icon.Ticket size={22} color="#7ab8ff" />
            </div>
            <div>
              <div style={s.headerTitle}>New Support Ticket</div>
              <div style={s.headerSub}>Fill out the form below to submit a request</div>
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose} title="Close">
            <Icon.Close size={13} color="#4a6a8a" />
          </button>
        </div>

        {/* ── Steps ── */}
        <div style={s.steps}>
          {STEPS.map((st, i) => {
            const active = step === i;
            const isDone = step > i;
            const stepStyle = active ? s.stepActive : isDone ? s.stepDone : s.stepIdle;
            const iconColor = active ? "#579dff" : isDone ? "#4bce97" : "#3d6080";
            return (
              <React.Fragment key={st.id}>
                <button
                  onClick={() => (isDone || active) && setStep(i)}
                  style={{ ...s.stepItem, ...stepStyle }}
                >
                  {isDone ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6" fill="rgba(75,206,151,.2)" stroke="#4bce97" strokeWidth="1.2"/>
                      <path d="M4 7l2 2 4-4" stroke="#4bce97" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <st.IconComp size={14} color={iconColor} />
                  )}
                  <span style={s.stepLabel}>{st.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div style={{ ...s.stepLine, background: isDone ? "#3b82f6" : "#1e3a55" }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ── */}
        <div style={s.body}>

          {/* STEP 0 — Basics */}
          {step === 0 && (
            <div style={s.grid2}>
              <Field label="Request Subject" required>
                <input style={s.input} name="Subject" value={form.Subject} onChange={handle}
                  placeholder="e.g. Cannot access VPN" autoFocus
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
              <Field label="Issue Type">
                <input style={s.input} name="IssueType" value={form.IssueType} onChange={handle}
                  placeholder="e.g. Bug, Feature Request"
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
              <Field label="Summary" span2>
                <input style={s.input} name="Summary" value={form.Summary} onChange={handle}
                  placeholder="One-line summary of the issue"
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
              <Field label="Email Address">
                <input style={s.input} name="Email" type="email" value={form.Email} onChange={handle}
                  placeholder="your.email@company.com"
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
              <Field label="Category">
                <Sel name="Category" value={form.Category} onChange={handle}
                  options={["General - IT Support","Network","Software","Hardware","Security","Other"]} />
              </Field>
              <Field label="Priority">
                <div style={s.pillGroup}>
                  {[["Low","#4bce97"],["Medium","#faa53d"],["High","#f87168"]].map(([p, c]) => (
                    <PillBtn key={p} label={p} active={form.Priority === p} color={c} onClick={() => set("Priority", p)} />
                  ))}
                </div>
              </Field>
              <Field label="Severity">
                <Sel name="Severity" value={form.Severity} onChange={handle}
                  options={["Sev-0","Sev-1","Sev-2","Sev-3"]} />
              </Field>
            </div>
          )}

          {/* STEP 1 — Details */}
          {step === 1 && (
            <div style={s.grid2}>
              <Field label="Affected Services">
                <Sel name="Affected" value={form.Affected} onChange={handle}
                  options={affectedServices.map(x => x.affectedName)} placeholder="Select service…" />
              </Field>
              <Field label="Components">
                <Sel name="Components" value={form.Components} onChange={handle}
                  options={components.map(x => x.componentName)} placeholder="Select component…" />
              </Field>
              <Field label="Urgency" span2>
                <div style={s.pillGroup}>
                  {[["Low","#4bce97"],["Medium","#579dff"],["High","#faa53d"],["Critical","#f87168"]].map(([u, c]) => (
                    <PillBtn key={u} label={u} active={form.Urgency === u} color={c} onClick={() => set("Urgency", u)} />
                  ))}
                </div>
              </Field>
              <Field label="Linked Issues">
                <Sel name="Linked" value={form.Linked} onChange={handle}
                  options={["None","Cloned","Relates","Blocks"]} />
              </Field>
              <Field label="Original Estimate">
                <input style={s.input} name="Estimate" value={form.Estimate} onChange={handle}
                  placeholder="e.g. 2w 3d 5h"
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
              <Field label="Due Date" span2>
                <input style={s.input} name="Deadline" type="datetime-local" value={form.Deadline} onChange={handle}
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"} />
              </Field>
            </div>
          )}

          {/* STEP 2 — Assignment */}
          {step === 2 && (
            <div style={s.grid2}>
              <Field label="Reporter">
                <Sel name="Reporter" value={form.Reporter} onChange={handle}
                  options={reporters.map(r => r.fullNames)} placeholder="Select reporter…" />
              </Field>
              <Field label="Assignee">
                <Sel name="Assignees" value={form.Assignees} onChange={handle}
                  options={reporters.map(r => r.fullNames)} placeholder="Select assignee…" />
              </Field>
              <Field label="Team">
                <Sel name="Team" value={form.Team} onChange={handle}
                  options={teams.map(t => t.teamName)} placeholder="Select team…" />
              </Field>
              {selectedReporter && (
                <div style={s.reporterCard}>
                  <img
                    src={`${config.API_BASE_URL}/Admin/get-profile-picture/${selectedReporter.username}`}
                    alt={selectedReporter.fullNames}
                    style={s.reporterAvatar}
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedReporter.fullNames)}&background=3b82f6&color=fff&size=64`;
                    }}
                  />
                  <div>
                    <div style={s.reporterName}>{selectedReporter.fullNames}</div>
                    <div style={s.reporterMeta}>{selectedReporter.email || selectedReporter.username}</div>
                  </div>
                  <div style={s.reporterBadge}>Reporter</div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Description + Attachment */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="Description" required>
                <textarea
                  style={{ ...s.input, resize: "vertical", minHeight: 150, lineHeight: 1.65 }}
                  name="Description" value={form.Description} onChange={handle}
                  placeholder="Describe the issue in detail — what happened, steps to reproduce, expected vs actual behavior…"
                  autoFocus
                  onFocus={e => e.target.style.borderColor = "#579dff"}
                  onBlur={e => e.target.style.borderColor = "#1e3a55"}
                />
              </Field>
              <Field label="Attachment">
                <div
                  style={{ ...s.dropzone, ...(dragActive ? s.dropzoneActive : {}) }}
                  onClick={() => fileRef.current?.click()}
                  onDrop={handleFileDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                >
                  {attachment ? (
                    <AttachPreview file={attachment} onRemove={(e) => { e.stopPropagation(); setAttachment(null); }} />
                  ) : (
                    <>
                      <Icon.Paperclip size={28} color={dragActive ? "#579dff" : "#2d5a8a"} />
                      <div style={{ fontSize: 13, color: "#8aaccc", fontWeight: 500 }}>
                        Click or drag & drop a file here
                      </div>
                      <div style={{ fontSize: 11, color: "#2d5a8a" }}>PNG, JPG, PDF, DOC — up to 10MB</div>
                    </>
                  )}
                </div>
                <input ref={fileRef} type="file" style={{ display: "none" }}
                  onChange={(e) => { setAttachment(e.target.files[0]); e.target.value = ""; }} />
              </Field>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={s.footer}>
          <button style={s.cancelBtn} onClick={onClose}
            onMouseEnter={e => e.currentTarget.style.color = "#8aaccc"}
            onMouseLeave={e => e.currentTarget.style.color = "#4a6a8a"}>
            Cancel
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button style={s.backBtn} onClick={() => setStep(st => st - 1)}>
                <Icon.ArrowLeft size={13} color="#8aaccc" /> Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                style={{ ...s.nextBtn, opacity: canNext() ? 1 : 0.4, cursor: canNext() ? "pointer" : "not-allowed" }}
                onClick={() => canNext() && setStep(st => st + 1)}
              >
                Next <Icon.ArrowRight size={13} color="#7ab8ff" />
              </button>
            ) : (
              <button
                style={{ ...s.submitBtn, opacity: !submitting && canNext() ? 1 : 0.4, cursor: !submitting && canNext() ? "pointer" : "not-allowed" }}
                onClick={() => !submitting && canNext() && handleSubmit()}
                disabled={submitting}
              >
                {submitting ? (
                  <><Spinner /> Submitting…</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4bce97" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Create Ticket
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      {children}
    </div>
  );
}

function Field({ label, required, children, span2 }) {
  return (
    <div style={span2 ? { gridColumn: "1/-1" } : {}}>
      <label style={s.label}>
        {label}{required && <span style={{ color: "#f87168" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Sel({ name, value, onChange, options = [], placeholder }) {
  return (
    <select name={name} value={value} onChange={onChange} style={s.input}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function PillBtn({ label, active, color, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "6px 16px", borderRadius: 20,
      border: `1.5px solid ${active ? color : "#1e3a55"}`,
      background: active ? `${color}22` : "transparent",
      color: active ? color : "#4a6a8a",
      fontSize: 12, fontWeight: active ? 700 : 500,
      cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
    }}>{label}</button>
  );
}

function AttachPreview({ file, onRemove }) {
  const [imgSrc, setImgSrc] = useState(null);
  useEffect(() => {
    if (!file?.type?.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onloadend = () => setImgSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);
  const isImg = file.type?.startsWith("image/");
  const size = file.size < 1024 ? `${file.size} B` : file.size < 1048576 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / 1048576).toFixed(1)} MB`;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
      {imgSrc
        ? <img src={imgSrc} alt={file.name} style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", border: "1px solid #1e3a55" }} />
        : (
          <div style={{ width: 52, height: 52, borderRadius: 10, background: "#0b1826", border: "1px solid #1e3a55", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon.FileDoc size={24} color="#4a6a8a" />
          </div>
        )
      }
      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#dce8f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>{file.name}</div>
        <div style={{ fontSize: 11, color: "#4a6a8a", marginTop: 3 }}>{size}</div>
      </div>
      <button onClick={onRemove} style={{ background: "#f8716822", border: "1px solid #f8716840", borderRadius: 8, color: "#f87168", padding: "5px 10px", cursor: "pointer", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
        <Icon.Close size={11} color="#f87168" /> Remove
      </button>
    </div>
  );
}

function Spinner() {
  return <span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(255,255,255,.25)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "ctspin .6s linear infinite", marginRight: 7, verticalAlign: "middle" }} />;
}

function SpinStyle() {
  return (
    <style>{`
      @keyframes ctspin { to { transform: rotate(360deg); } }
      @keyframes successPulse {
        0% { transform: scale(1); opacity: .6; }
        50% { transform: scale(1.12); opacity: .25; }
        100% { transform: scale(1); opacity: .6; }
      }
    `}</style>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(4,8,18,.9)", backdropFilter: "blur(12px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  modal: {
    background: "linear-gradient(160deg,#0d1b2e 0%,#0a1628 55%,#060f1e 100%)",
    border: "1px solid #1a3050",
    borderRadius: 20, width: "100%", maxWidth: 640,
    maxHeight: "92vh", display: "flex", flexDirection: "column",
    boxShadow: "0 32px 80px rgba(0,0,0,.95), inset 0 1px 0 rgba(87,157,255,.08)",
    overflow: "hidden", fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #1a3050",
    background: "rgba(87,157,255,.03)", flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  headerIconBox: {
    width: 46, height: 46, borderRadius: 13,
    background: "linear-gradient(135deg, #1a3a5c 0%, #0f2440 100%)",
    border: "1px solid #2d5a8a",
    boxShadow: "0 4px 16px rgba(87,157,255,.15), inset 0 1px 0 rgba(255,255,255,.06)",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: { fontSize: 17, fontWeight: 700, color: "#dce8f5" },
  headerSub: { fontSize: 11, color: "#3d6080", marginTop: 2 },
  closeBtn: {
    background: "rgba(255,255,255,.04)", border: "1px solid #1a3050",
    borderRadius: 8, color: "#3d6080", width: 32, height: 32,
    cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", fontFamily: "inherit",
    transition: "background .15s",
  },
  steps: {
    display: "flex", alignItems: "center",
    padding: "14px 24px", borderBottom: "1px solid #0d1e30", flexShrink: 0,
  },
  stepItem: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "6px 12px", borderRadius: 10, border: "none",
    cursor: "pointer", fontSize: 12, fontWeight: 600,
    fontFamily: "inherit", transition: "all .15s", flexShrink: 0,
  },
  stepActive: { background: "rgba(87,157,255,.18)", color: "#579dff", border: "1px solid rgba(87,157,255,.35)" },
  stepDone:   { background: "rgba(75,206,151,.1)",  color: "#4bce97", border: "1px solid rgba(75,206,151,.25)" },
  stepIdle:   { background: "transparent", color: "#3d6080", border: "1px solid transparent" },
  stepLabel:  { fontSize: 11 },
  stepLine:   { flex: 1, height: 1, minWidth: 10, margin: "0 4px", transition: "background .3s" },
  body: {
    flex: 1, overflowY: "auto", padding: "22px 24px",
    scrollbarWidth: "thin", scrollbarColor: "#1a3050 transparent",
  },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  label: {
    display: "block", fontSize: 11, fontWeight: 700,
    color: "#3d6a8a", textTransform: "uppercase",
    letterSpacing: ".06em", marginBottom: 6,
  },
  input: {
    width: "100%", background: "#080f1c", border: "1px solid #1e3a55",
    borderRadius: 10, padding: "10px 13px", color: "#dce8f5",
    fontSize: 13, fontFamily: "inherit", outline: "none",
    transition: "border-color .15s", boxSizing: "border-box",
  },
  pillGroup: { display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 2 },
  dropzone: {
    border: "2px dashed #1a3050", borderRadius: 12, padding: "28px 20px",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", gap: 8, cursor: "pointer",
    transition: "all .15s", minHeight: 110, background: "rgba(255,255,255,.01)",
  },
  dropzoneActive: { borderColor: "#579dff", background: "rgba(87,157,255,.05)" },
  footer: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "15px 24px", borderTop: "1px solid #0d1e30",
    background: "rgba(0,0,0,.4)", flexShrink: 0,
  },
  cancelBtn: {
    padding: "9px 18px", borderRadius: 10, border: "1px solid #1a3050",
    background: "transparent", color: "#4a6a8a", fontSize: 13,
    cursor: "pointer", fontFamily: "inherit", transition: "color .15s",
  },
  backBtn: {
    padding: "9px 18px", borderRadius: 10, border: "1px solid #1a3050",
    background: "rgba(255,255,255,.03)", color: "#8aaccc", fontSize: 13,
    cursor: "pointer", fontFamily: "inherit",
    display: "flex", alignItems: "center", gap: 6,
  },
  nextBtn: {
    padding: "9px 24px", borderRadius: 10, border: "none",
    background: "linear-gradient(135deg,#1c4a8a,#152a60)",
    color: "#7ab8ff", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 2px 14px rgba(30,74,138,.45)",
    transition: "opacity .15s",
    display: "flex", alignItems: "center", gap: 6,
  },
  submitBtn: {
    display: "flex", alignItems: "center",
    padding: "9px 24px", borderRadius: 10, border: "none",
    background: "linear-gradient(135deg,#1a6a3a,#0f4020)",
    color: "#4bce97", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 2px 14px rgba(75,206,151,.2)",
    transition: "opacity .15s",
  },
  reporterCard: {
    gridColumn: "1/-1",
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 16px", borderRadius: 12,
    background: "rgba(87,157,255,.06)", border: "1px solid rgba(87,157,255,.18)",
    position: "relative",
  },
  reporterAvatar: {
    width: 42, height: 42, borderRadius: "50%",
    objectFit: "cover", border: "2px solid rgba(87,157,255,.4)",
  },
  reporterName: { fontSize: 13, fontWeight: 600, color: "#dce8f5" },
  reporterMeta: { fontSize: 11, color: "#3d6080", marginTop: 2 },
  reporterBadge: {
    marginLeft: "auto", fontSize: 10, fontWeight: 700,
    color: "#579dff", background: "rgba(87,157,255,.12)",
    border: "1px solid rgba(87,157,255,.25)", borderRadius: 20, padding: "2px 10px",
  },
  // Success screen
  successBox: {
    background: "linear-gradient(160deg,#0d1b2e,#060f1e)",
    border: "1px solid #1a3050", borderRadius: 20,
    padding: "52px 48px", textAlign: "center",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
    boxShadow: "0 32px 80px rgba(0,0,0,.95)",
    fontFamily: "'Segoe UI', system-ui, sans-serif", minWidth: 360,
  },
  successRingOuter: {
    position: "absolute",
    width: 96, height: 96, borderRadius: "50%",
    border: "1.5px solid rgba(75,206,151,.25)",
    animation: "successPulse 2.5s ease-in-out infinite",
    top: "50%", left: "50%", transform: "translate(-50%, -50%)",
  },
  successRingInner: {
    width: 76, height: 76, borderRadius: "50%",
    background: "rgba(75,206,151,.1)", border: "2px solid rgba(75,206,151,.35)",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative",
  },
  successTitle: { fontSize: 24, fontWeight: 700, color: "#dce8f5", margin: 0 },
  successNum: {
    fontFamily: "monospace", fontSize: 12, letterSpacing: ".04em",
    color: "#579dff", background: "rgba(87,157,255,.1)",
    border: "1px solid rgba(87,157,255,.2)", borderRadius: 8, padding: "6px 18px",
  },
  successSub: { fontSize: 12, color: "#3d6080", margin: 0 },
  successBtn: {
    marginTop: 8, padding: "10px 36px", borderRadius: 10,
    border: "1px solid rgba(87,157,255,.25)", background: "rgba(87,157,255,.1)",
    color: "#579dff", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  },
};